import {
  ActionIcon,
  Box,
  Center,
  CloseButton,
  Group,
  Loader,
  Popover,
  ScrollArea,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import { useVirtualizer } from '@tanstack/react-virtual'
import {
  CalendarIcon,
  CaretDownIcon,
  CaretUpIcon,
  HashIcon,
  MagnifyingGlassIcon,
  RulerIcon,
  TextAaIcon,
} from '@phosphor-icons/react'
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import {
  BOGGLE_RUN_SUMMARY_RATINGS,
  type BoggleRunSummaryRatingId,
} from '~/boggle/config/runSummaryScore'
import type {
  BoggleUnviewedWord,
  BoggleWordLexicalTag,
  BoggleWordStat,
} from '~/boggle/progress/types'
import { BogglePlayedDateTime } from '~/boggle/ui/atoms/BogglePlayedDateTime'
import { IconGlyph } from '~/boggle/ui/atoms/Icons'
import { UnviewedNewWordIndicator } from '~/boggle/ui/unviewed/UnviewedItemIndicators'
import { RatingBadge } from '~/boggle/ui/ratings/RatingBadge'
import { RatingFilter, type RatingFilterValue } from '~/boggle/ui/ratings/RatingFilter'

import classes from './Words.module.css'

type WordsLengthFilter = 'all' | `${number}`
type WordsRatingFilter = RatingFilterValue
type WordsSortId = 'alphabetical' | 'lastPlayed' | 'length' | 'timesPlayed'
/** Tags shown in Words filters and rows (`double-letter` is suppressed). */
type WordsTagFilter = 'all' | Exclude<BoggleWordLexicalTag, 'double-letter'>

export type BoggleWordsFilterState = {
  length: WordsLengthFilter
  rating: WordsRatingFilter
  search: string
  tag: WordsTagFilter
}

const DEFAULT_FILTERS: BoggleWordsFilterState = {
  length: 'all',
  rating: 'all',
  search: '',
  tag: 'all',
}

const LEXICAL_TAG_LABELS: Record<
  Exclude<BoggleWordLexicalTag, 'double-letter'>,
  string
> = {
  alphabetical: 'Alphabetical',
  anadrome: 'Anadrome',
  isogram: 'Isogram',
  palindrome: 'Palindrome',
  straight: 'Straight',
}

const LEXICAL_TAGS = Object.keys(LEXICAL_TAG_LABELS) as Exclude<
  BoggleWordLexicalTag,
  'double-letter'
>[]
/** Always shown; diamond / ace chips appear only when stats use them. */
const BASE_WORD_RATING_FILTERS: readonly BoggleRunSummaryRatingId[] = [
  'bronze',
  'silver',
  'gold',
]

const WORDS_ROW_GAP = 6
/** Initial guess before measureElement runs; rows with wrapped tags are taller. */
const WORDS_ROW_ESTIMATE_PX = 52
const WORDS_VIRTUAL_OVERSCAN = 10

function buildWordRatingFilterValues(
  stats: readonly BoggleWordStat[],
): WordsRatingFilter[] {
  const tiersPresent = new Set(stats.map(stat => stat.highestRatingId))
  const extraTierIds = BOGGLE_RUN_SUMMARY_RATINGS.map(rating => rating.id).filter(
    id =>
      id !== 'none' &&
      !(BASE_WORD_RATING_FILTERS as readonly string[]).includes(id) &&
      tiersPresent.has(id),
  )
  return ['all', ...BASE_WORD_RATING_FILTERS, ...extraTierIds]
}

function compareBoggleWordStats(a: BoggleWordStat, b: BoggleWordStat) {
  const normalizedOrder = a.normalizedWord.localeCompare(b.normalizedWord)
  return normalizedOrder !== 0
    ? normalizedOrder
    : a.firstPlayedAt.localeCompare(b.firstPlayedAt)
}

function defaultSortDescending(id: WordsSortId): boolean {
  return id === 'timesPlayed' || id === 'lastPlayed'
}

function compareWordsVisibleOrderTieBreak(
  a: BoggleWordStat,
  b: BoggleWordStat,
  descending: boolean,
) {
  const firstPlayedCmp = a.firstPlayedAt.localeCompare(b.firstPlayedAt)
  if (firstPlayedCmp !== 0) {
    return descending ? -firstPlayedCmp : firstPlayedCmp
  }
  const wordCmp = a.normalizedWord.localeCompare(b.normalizedWord)
  return descending ? -wordCmp : wordCmp
}

export function compareWordsVisibleOrder(
  a: BoggleWordStat,
  b: BoggleWordStat,
  sortId: WordsSortId,
  descending: boolean,
) {
  switch (sortId) {
    case 'length': {
      const cmp = a.length - b.length
      if (cmp !== 0) {
        return descending ? -cmp : cmp
      }
      break
    }
    case 'lastPlayed': {
      const cmp = a.lastPlayedAt.localeCompare(b.lastPlayedAt)
      if (cmp !== 0) {
        return descending ? -cmp : cmp
      }
      break
    }
    case 'timesPlayed': {
      const cmp = a.timesPlayed - b.timesPlayed
      if (cmp !== 0) {
        return descending ? -cmp : cmp
      }
      break
    }
    case 'alphabetical':
    default: {
      const cmp = a.normalizedWord.localeCompare(b.normalizedWord)
      if (cmp !== 0) {
        return descending ? -cmp : cmp
      }
      const tie = a.firstPlayedAt.localeCompare(b.firstPlayedAt)
      return descending ? -tie : tie
    }
  }
  return compareWordsVisibleOrderTieBreak(a, b, descending)
}

export function sortBoggleWordStats(stats: readonly BoggleWordStat[]) {
  return [...stats].sort(compareBoggleWordStats)
}

export function filterBoggleWordStats(
  stats: readonly BoggleWordStat[],
  filters: BoggleWordsFilterState,
) {
  const normalizedSearch = filters.search.trim().toLowerCase()

  return stats.filter(stat => {
    if (normalizedSearch.length > 0 && !stat.normalizedWord.includes(normalizedSearch)) {
      return false
    }
    if (filters.length !== 'all' && stat.length !== Number(filters.length)) {
      return false
    }
    if (filters.rating !== 'all' && stat.highestRatingId !== filters.rating) {
      return false
    }
    if (filters.tag !== 'all' && !stat.lexicalTags.includes(filters.tag)) {
      return false
    }
    return true
  })
}

type WordsSortControl = {
  icon: typeof TextAaIcon
  id: WordsSortId
  labelAsc: string
  labelDesc: string
  testId: string
}

export type BoggleWordListRowItem = {
  coloredLetters?: ReadonlyArray<{ color?: string; letter: string; textShadow?: string }>
  displayWord: string
  /** Seconds since the previous accepted word (run details only). */
  gapSincePreviousSeconds?: number
  gapSincePreviousColor?: string
  /** Run details only — word was played again after an earlier accept this run. */
  isReplay?: boolean
  length: number
  lexicalTags?: readonly BoggleWordLexicalTag[]
  normalizedWord: string
  /** Seconds since run start when the word was accepted (run history only). */
  playedAtSeconds?: number
  ratingId?: BoggleRunSummaryRatingId
}

function formatWordPlayedAtSeconds(seconds: number) {
  return `${seconds.toFixed(1)}s`
}

function formatWordGapSincePrevious(seconds: number) {
  return `+${seconds.toFixed(1)}s`
}

const WORDS_SORT_CONTROLS: readonly WordsSortControl[] = [
  {
    icon: CalendarIcon,
    id: 'lastPlayed',
    labelAsc: 'Sort by last played (oldest first)',
    labelDesc: 'Sort by last played (newest first)',
    testId: 'words-sort-last-played',
  },
  {
    icon: TextAaIcon,
    id: 'alphabetical',
    labelAsc: 'Sort A–Z',
    labelDesc: 'Sort Z–A',
    testId: 'words-sort-alphabetical',
  },
  {
    icon: RulerIcon,
    id: 'length',
    labelAsc: 'Sort by length (short first)',
    labelDesc: 'Sort by length (long first)',
    testId: 'words-sort-length',
  },
  {
    icon: HashIcon,
    id: 'timesPlayed',
    labelAsc: 'Sort by times played (fewest first)',
    labelDesc: 'Sort by times played (most first)',
    testId: 'words-sort-times-played',
  },
]

/**
 * Screen-capable Words browser. Hosts provide stats/loading state; this view
 * owns presentation and filters so the same surface can later live outside a modal.
 */
export function Words({
  failed,
  loading,
  onClose,
  onWordViewed,
  sessionNewWords = [],
  stats,
}: {
  failed?: boolean
  loading: boolean
  onClose?: () => void
  onWordViewed?: (normalizedWord: string) => void
  /** Snapshot taken when the lexicon opens; cleared from storage at that moment. */
  sessionNewWords?: readonly BoggleUnviewedWord[]
  stats: readonly BoggleWordStat[]
}) {
  const [filters, setFilters] = useState<BoggleWordsFilterState>(DEFAULT_FILTERS)
  const [sort, setSort] = useState<{ descending: boolean; id: WordsSortId }>({
    descending: defaultSortDescending('lastPlayed'),
    id: 'lastPlayed',
  })
  const [debouncedSearch] = useDebouncedValue(filters.search, 150)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const scrollViewportRef = useRef<HTMLDivElement>(null)
  const [selectedNormalizedWord, setSelectedNormalizedWord] = useState<string | null>(
    null,
  )

  const sortedStats = useMemo(() => sortBoggleWordStats(stats), [stats])
  const ratingFilterValues = useMemo(() => buildWordRatingFilterValues(stats), [stats])

  useEffect(() => {
    if (!ratingFilterValues.includes(filters.rating)) {
      setFilters(current => ({ ...current, rating: 'all' }))
    }
  }, [filters.rating, ratingFilterValues])

  useEffect(() => {
    setSelectedNormalizedWord(current =>
      current !== null && stats.some(s => s.normalizedWord === current) ? current : null,
    )
  }, [stats])

  const availableLengths = useMemo(
    () => [...new Set(sortedStats.map(stat => stat.length))].sort((a, b) => a - b),
    [sortedStats],
  )
  const visibleStats = useMemo(() => {
    const filtered = filterBoggleWordStats(sortedStats, {
      ...filters,
      search: debouncedSearch,
    })
    return [...filtered].sort((a, b) =>
      compareWordsVisibleOrder(a, b, sort.id, sort.descending),
    )
  }, [debouncedSearch, filters, sort.descending, sort.id, sortedStats])

  const lexiconFilterActive = useMemo(
    () =>
      debouncedSearch.trim() !== '' ||
      filters.length !== 'all' ||
      filters.rating !== 'all' ||
      filters.tag !== 'all',
    [debouncedSearch, filters.length, filters.rating, filters.tag],
  )

  const handleSortClick = (id: WordsSortId) => {
    setSort(prev =>
      prev.id === id
        ? { ...prev, descending: !prev.descending }
        : { descending: defaultSortDescending(id), id },
    )
  }

  const sessionNewWordSet = useMemo(
    () => new Set(sessionNewWords.map(entry => entry.normalizedWord)),
    [sessionNewWords],
  )

  const onWordViewedRef = useRef(onWordViewed)
  onWordViewedRef.current = onWordViewed

  const toggleRowSelection = useCallback(
    (normalizedWord: string) => {
      setSelectedNormalizedWord(prev => {
        const next = prev === normalizedWord ? null : normalizedWord
        if (next !== null && sessionNewWordSet.has(normalizedWord)) {
          onWordViewedRef.current?.(normalizedWord)
        }
        return next
      })
    },
    [sessionNewWordSet],
  )

  const showRatingBadge = filters.rating === 'all'

  const virtualizer = useVirtualizer({
    count: visibleStats.length,
    estimateSize: () => WORDS_ROW_ESTIMATE_PX + WORDS_ROW_GAP,
    getScrollElement: () => scrollViewportRef.current,
    initialRect: { height: 500, width: 700 },
    overscan: WORDS_VIRTUAL_OVERSCAN,
  })

  const virtualItems = virtualizer.getVirtualItems()
  const useVirtualRows = virtualItems.length > 0

  useEffect(() => {
    if (!useVirtualRows || selectedNormalizedWord === null || visibleStats.length === 0) {
      return
    }
    const visibleIndices = new Set(virtualizer.getVirtualItems().map(item => item.index))
    const selectedIndex = visibleStats.findIndex(
      stat => stat.normalizedWord === selectedNormalizedWord,
    )
    if (selectedIndex >= 0 && !visibleIndices.has(selectedIndex)) {
      setSelectedNormalizedWord(null)
    }
  }, [selectedNormalizedWord, useVirtualRows, virtualizer, visibleStats])

  useEffect(() => {
    virtualizer.scrollToIndex(0)
  }, [
    debouncedSearch,
    filters.length,
    filters.rating,
    filters.tag,
    sort.descending,
    sort.id,
  ])

  const handlePopoverChange = useCallback(
    (open: boolean) => {
      if (!open && selectedNormalizedWord !== null) {
        setSelectedNormalizedWord(null)
      }
    },
    [selectedNormalizedWord],
  )

  return (
    <Stack
      data-testid="boggle-words"
      gap={12}
      h="100%"
      mih={0}
      pb={14}
      style={{ overflow: 'hidden' }}
    >
      {onClose ? (
        <Box pos="relative" w="100%">
          <CloseButton
            aria-label="Close words"
            c="white"
            data-testid="words-close"
            iconSize={28}
            onClick={onClose}
            pos="absolute"
            right={0}
            top={0}
            variant="transparent"
          />
        </Box>
      ) : null}

      <Group justify="space-between" w="100%" wrap="nowrap" px={14}>
        <Group gap={8} grow miw={0} wrap="nowrap">
          <Select
            aria-label="Filter by length"
            data-testid="words-filter-length"
            data={[
              { label: 'Any length', value: 'all' },
              ...availableLengths.map(length => ({
                label: `${length} letters`,
                value: String(length),
              })),
            ]}
            allowDeselect={false}
            onChange={value =>
              setFilters(current => ({
                ...current,
                length: (value ?? 'all') as WordsLengthFilter,
              }))
            }
            value={filters.length}
          />
          <Select
            aria-label="Filter by lexical tag"
            data-testid="words-filter-tag"
            data={[
              { label: 'All tags', value: 'all' },
              ...LEXICAL_TAGS.map(tag => ({
                label: LEXICAL_TAG_LABELS[tag],
                value: tag,
              })),
            ]}
            allowDeselect={false}
            onChange={value =>
              setFilters(current => ({
                ...current,
                tag: (value ?? 'all') as WordsTagFilter,
              }))
            }
            value={filters.tag}
          />
        </Group>
        <Box pos="relative" style={{ flexShrink: 0 }} w={140}>
          <RatingFilter
            ariaLabelPrefix="Filter words by"
            onChange={rating =>
              setFilters(current => ({
                ...current,
                rating,
              }))
            }
            testIdPrefix="words-filter-rating"
            value={filters.rating}
            values={ratingFilterValues}
            variant="select"
          />
        </Box>
      </Group>

      <Box
        flex={1}
        mih={0}
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Center data-testid="words-loading" flex={1} mih={0}>
            <Loader color="gray" type="dots" />
          </Center>
        ) : failed ? (
          <Center data-testid="words-error" flex={1} mih={0}>
            <Text c="rgba(232, 224, 210, 0.7)" fz={15}>
              Unable to load words.
            </Text>
          </Center>
        ) : stats.length === 0 ? (
          <Center data-testid="words-empty" flex={1} mih={0}>
            <Text c="rgba(232, 224, 210, 0.7)" fz={15}>
              No words found yet
            </Text>
          </Center>
        ) : (
          <Stack gap={2} flex={1} mih={0}>
            <Group
              align="center"
              justify="space-between"
              style={{ overflow: 'visible' }}
              wrap="nowrap"
              px={14}
            >
              <Group align="center" gap={8} miw={0} wrap="nowrap" style={{ flex: 1 }}>
                <Text c="white" fz={17} fw={400} lh={1} pl={8}>
                  {lexiconFilterActive ? (
                    <>
                      <Text component="span" fz={17} fw={400} lh={1} opacity={1}>
                        {visibleStats.length}
                      </Text>
                      <Text component="span" fz={17} fw={400} lh={1} opacity={0.7}>
                        {' '}
                        / {sortedStats.length} words found
                      </Text>
                    </>
                  ) : (
                    <Text component="span" fz={17} fw={400} lh={1} opacity={0.7}>
                      {visibleStats.length}{' '}
                      {visibleStats.length === 1 ? 'word found' : 'words found'}
                    </Text>
                  )}
                </Text>
                {/* <TextInput
                ref={searchInputRef}
                aria-label="Search words"
                className={classes.searchInput}
                data-testid="words-filter-search"
                leftSection={<IconGlyph icon={MagnifyingGlassIcon} size={18} color="rgba(255, 255, 255, 0.5)" />}
                onChange={event => {
                  const search = event.currentTarget.value
                  setFilters(current => ({ ...current, search }))
                }}
                // placeholder="Search"
                value={filters.search}
                variant="unstyled"
              /> */}
              </Group>
              <Group gap={4} style={{ flexShrink: 0 }} wrap="nowrap" pb={12}>
                {WORDS_SORT_CONTROLS.map(control => {
                  const active = sort.id === control.id
                  const tooltip = sort.descending ? control.labelDesc : control.labelAsc
                  const DirectionIcon = sort.descending ? CaretUpIcon : CaretDownIcon
                  return (
                    <Tooltip key={control.id} label={tooltip} openDelay={400}>
                      <Box className={classes.sortControlWrap}>
                        <ActionIcon
                          aria-label={tooltip}
                          aria-pressed={active}
                          bdrs={8}
                          classNames={{
                            root: active ? classes.sortControlActive : undefined,
                          }}
                          color="gray"
                          data-testid={control.testId}
                          onClick={() => handleSortClick(control.id)}
                          size={32}
                          variant="subtle"
                        >
                          <IconGlyph color="currentColor" icon={control.icon} size={18} />
                        </ActionIcon>
                        {active ? (
                          <Box aria-hidden className={classes.sortChevronBelow}>
                            <IconGlyph
                              color="currentColor"
                              icon={DirectionIcon}
                              size={10}
                            />
                          </Box>
                        ) : null}
                      </Box>
                    </Tooltip>
                  )
                })}
              </Group>
            </Group>
            <Box
              flex={1}
              mih={0}
              pos="relative"
              style={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
              <ScrollArea
                flex={1}
                mih={0}
                px={14}
                style={{ flex: 1, minHeight: 0 }}
                type="auto"
                viewportRef={scrollViewportRef}
              >
                {visibleStats.length === 0 ? (
                  <Center data-testid="words-filter-empty" mih={120}>
                    <Text c="rgba(232, 224, 210, 0.7)" fz={15}>
                      No matching words.
                    </Text>
                  </Center>
                ) : (
                  <Box
                    data-testid="words-list"
                    pos="relative"
                    pr={8}
                    style={{
                      height: useVirtualRows ? virtualizer.getTotalSize() : undefined,
                    }}
                  >
                    {useVirtualRows
                      ? virtualItems.map(virtualRow => {
                          const stat = visibleStats[virtualRow.index]
                          if (!stat) {
                            return null
                          }
                          return (
                            <Box
                              key={stat.normalizedWord}
                              data-index={virtualRow.index}
                              left={0}
                              pos="absolute"
                              ref={virtualizer.measureElement}
                              top={0}
                              w="100%"
                              style={{
                                paddingBottom: WORDS_ROW_GAP,
                                transform: `translateY(${virtualRow.start}px)`,
                              }}
                            >
                              <BoggleWordStatRow
                                onPopoverChange={handlePopoverChange}
                                onToggleSelect={toggleRowSelection}
                                selected={selectedNormalizedWord === stat.normalizedWord}
                                showNewIndicator={sessionNewWordSet.has(
                                  stat.normalizedWord,
                                )}
                                showRatingBadge={showRatingBadge}
                                stat={stat}
                              />
                            </Box>
                          )
                        })
                      : visibleStats.map(stat => (
                          <Box key={stat.normalizedWord} pb={WORDS_ROW_GAP}>
                            <BoggleWordStatRow
                              onPopoverChange={handlePopoverChange}
                              onToggleSelect={toggleRowSelection}
                              selected={selectedNormalizedWord === stat.normalizedWord}
                              showNewIndicator={sessionNewWordSet.has(
                                stat.normalizedWord,
                              )}
                              showRatingBadge={showRatingBadge}
                              stat={stat}
                            />
                          </Box>
                        ))}
                  </Box>
                )}
              </ScrollArea>
            </Box>
          </Stack>
        )}
      </Box>
    </Stack>
  )
}

export function BoggleWordList({
  emptyFallback,
  items,
  testId = 'words-list',
}: {
  emptyFallback?: ReactNode
  items: readonly BoggleWordListRowItem[]
  testId?: string
}) {
  return (
    <Stack gap={6} data-testid={testId} align="center">
      {items.length === 0
        ? emptyFallback
        : items.map(item => <BoggleWordListRow item={item} key={item.normalizedWord} />)}
    </Stack>
  )
}

export function BoggleWordListRow({
  item,
  onToggleSelect,
  selected,
  showNewIndicator = false,
  showRatingBadge = false,
}: {
  item: BoggleWordListRowItem
  onToggleSelect?: () => void
  selected?: boolean
  showNewIndicator?: boolean
  showRatingBadge?: boolean
}) {
  return (
    <BoggleWordListRowFrame
      item={item}
      onToggleSelect={onToggleSelect}
      selected={selected ?? false}
      showNewIndicator={showNewIndicator}
      showRatingBadge={showRatingBadge}
    />
  )
}

function statToRowItem(stat: BoggleWordStat): BoggleWordListRowItem {
  return {
    displayWord: stat.displayWord,
    length: stat.length,
    lexicalTags: stat.lexicalTags,
    normalizedWord: stat.normalizedWord,
    ratingId: stat.highestRatingId,
  }
}

/** Words lexicon row; selected row mounts the sole stats Popover. */
const BoggleWordStatRow = memo(function BoggleWordStatRow({
  onPopoverChange,
  onToggleSelect,
  selected,
  showNewIndicator,
  showRatingBadge,
  stat,
}: {
  onPopoverChange: (open: boolean) => void
  onToggleSelect: (normalizedWord: string) => void
  selected: boolean
  showNewIndicator: boolean
  showRatingBadge: boolean
  stat: BoggleWordStat
}) {
  const item = useMemo(() => statToRowItem(stat), [stat])
  const handleToggle = useCallback(() => {
    onToggleSelect(stat.normalizedWord)
  }, [onToggleSelect, stat.normalizedWord])

  const rowFrame = (
    <BoggleWordListRowFrame
      item={item}
      onToggleSelect={handleToggle}
      selected={selected}
      showNewIndicator={showNewIndicator}
      showRatingBadge={showRatingBadge}
    />
  )

  if (!selected) {
    return rowFrame
  }

  return (
    <Popover
      closeOnClickOutside
      closeOnEscape
      offset={2}
      onChange={onPopoverChange}
      opened
      position="bottom-start"
      shadow="md"
      styles={{ dropdown: { pointerEvents: 'none' } }}
      transitionProps={{ duration: 50 }}
    >
      <Popover.Target>{rowFrame}</Popover.Target>
      <BoggleWordListRowStatsDropdown
        firstPlayedAt={stat.firstPlayedAt}
        lastPlayedAt={stat.lastPlayedAt}
        normalizedWord={stat.normalizedWord}
        timesPlayed={stat.timesPlayed}
      />
    </Popover>
  )
})

export function BoggleWordListRowStatsDropdown({
  firstPlayedAt,
  lastPlayedAt,
  normalizedWord,
  timesPlayed,
}: {
  firstPlayedAt: string
  lastPlayedAt: string
  normalizedWord: string
  timesPlayed: number
}) {
  return (
    <Popover.Dropdown
      bd="1px solid rgba(255, 255, 255, 0.14)"
      bg="rgba(20, 18, 14, 0.97)"
      data-testid={`words-row-stats-dropdown-${normalizedWord}`}
      p={12}
    >
      <Group gap={40} justify="space-between" wrap="nowrap" align="flex-start">
        <Box>
          <Text c="rgba(232, 224, 210, 0.55)" fz={10} lh={1} tt="uppercase">
            Last played
          </Text>
          <Box mt={4}>
            <BogglePlayedDateTime
              invalid={{ date: '—', time: '—' }}
              isoString={lastPlayedAt}
              testIdPrefix={`words-row-stats-last-played-${normalizedWord}`}
            />
          </Box>
        </Box>
        <Box>
          <Text c="rgba(232, 224, 210, 0.55)" fz={10} lh={1} tt="uppercase">
            Plays
          </Text>
          <Text
            c="white"
            data-testid={`words-row-stats-plays-${normalizedWord}`}
            fz={14}
            lh={1.25}
            mt={4}
          >
            {timesPlayed}
          </Text>
        </Box>
      </Group>
    </Popover.Dropdown>
  )
}

const BoggleWordListRowFrame = forwardRef<
  HTMLDivElement,
  {
    item: BoggleWordListRowItem
    onToggleSelect?: () => void
    selected: boolean
    showNewIndicator: boolean
    showRatingBadge: boolean
  }
>(function BoggleWordListRowFrame(
  { item, onToggleSelect, selected, showNewIndicator, showRatingBadge },
  ref,
) {
  const tags = (item.lexicalTags ?? []).filter(
    (tag): tag is Exclude<BoggleWordLexicalTag, 'double-letter'> =>
      tag !== 'double-letter',
  )
  const ratingId = item.ratingId ?? 'none'
  const rowClassName = `${classes.row}${item.isReplay ? ` ${classes.rowRepeat}` : ''}${selected ? ` ${classes.rowSelected}` : ''}`
  const interactive = onToggleSelect !== undefined

  return (
    <Group
      ref={ref}
      aria-expanded={interactive ? selected : undefined}
      aria-haspopup={interactive ? 'dialog' : undefined}
      aria-pressed={interactive ? selected : undefined}
      className={rowClassName}
      data-selected={selected ? 'true' : 'false'}
      data-testid={`words-row-${item.normalizedWord}`}
      onClick={onToggleSelect}
      pos="relative"
      onKeyDown={
        interactive
          ? event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onToggleSelect?.()
              }
            }
          : undefined
      }
      role={interactive ? 'button' : 'listitem'}
      align="center"
      style={{ cursor: interactive ? 'pointer' : undefined, width: '100%' }}
      tabIndex={interactive ? 0 : undefined}
    >
      <Group align="center" gap={12} justify="space-between" wrap="nowrap" w="100%">
        <Group
          align="center"
          gap={10}
          miw={0}
          style={{ flex: '1 1 42%', minWidth: 0 }}
          wrap="nowrap"
        >
          <Text c="rgba(232, 224, 210, 0.62)" fz={15} lh={1}>
            {item.length}
          </Text>
          <UnviewedNewWordIndicator
            active={showNewIndicator}
            testId={showNewIndicator ? `words-row-new-${item.normalizedWord}` : undefined}
          >
            <Stack gap={2}>
              {item.coloredLetters && item.coloredLetters.length > 0 ? (
                <Text c="white" fw={500} fz={18} lh={1} ff="Noto Sans, sans-serif" lts={1}>
                  {item.coloredLetters.map((entry, index) => (
                    <Text
                      c={entry.color ?? 'white'}
                      component="span"
                      ff="Noto Sans, sans-serif"
                      fw={500}
                      fz={18}
                      key={`${item.normalizedWord}-${index}`}
                      lts={1}
                      lh={1}
                      style={entry.textShadow ? { textShadow: entry.textShadow } : undefined}
                    >
                      {entry.letter}
                    </Text>
                  ))}
                </Text>
              ) : (
                <Text c="white" fw={500} fz={18} lh={1} ff="Noto Sans, sans-serif" lts={1}>
                  {item.displayWord}
                </Text>
              )}
              {item.isReplay ? (
                <Text c="#ffd781" fz={10} lh={1} tt="uppercase">
                  Repeat
                </Text>
              ) : null}
            </Stack>
          </UnviewedNewWordIndicator>
        </Group>

        <Group
          align="center"
          gap={10}
          justify="flex-end"
          miw={0}
          style={{ flex: '1 1 52%', minWidth: 0 }}
          wrap="nowrap"
        >
          {tags.length > 0 ? (
            <Box className={classes.tagCluster}>
              {tags.map(tag => (
                <Text className={classes.tag} fz={11} key={tag} lh={1}>
                  {LEXICAL_TAG_LABELS[tag]}
                </Text>
              ))}
            </Box>
          ) : (
            <Box style={{ flex: '1 1 auto', minWidth: 0 }} />
          )}
          {item.playedAtSeconds !== undefined ? (
            <Stack gap={2} style={{ flexShrink: 0 }} ta="right">
              <Text
                c="rgba(232, 224, 210, 0.55)"
                ff="Noto Sans, sans-serif"
                fz={13}
                lh={1}
              >
                {formatWordPlayedAtSeconds(item.playedAtSeconds)}
              </Text>
              {item.gapSincePreviousSeconds !== undefined ? (
                <Text
                  c={item.gapSincePreviousColor ?? 'rgba(232, 224, 210, 0.55)'}
                  ff="Noto Sans, sans-serif"
                  fz={10}
                  lh={1}
                >
                  {formatWordGapSincePrevious(item.gapSincePreviousSeconds)}
                </Text>
              ) : null}
            </Stack>
          ) : null}
          {showRatingBadge ? (
            <Box
              display="flex"
              style={{ alignItems: 'center', flexShrink: 0 }}
              pos="relative"
              top={1}
            >
              <RatingBadge
                ratingId={ratingId}
                testId={`words-row-rating-${item.normalizedWord}`}
              />
            </Box>
          ) : null}
        </Group>
      </Group>
    </Group>
  )
})
